/**
 * SceneManager — Manages scene lifecycle: register, transition, update, resize.
 * Each scene is a PixiJS Container with lifecycle methods.
 */
import eventBus from './EventBus.js';

class SceneManager {
  constructor() {
    this._scenes = new Map();
    this._currentScene = null;
    this._currentName = null;
    this._stage = null;
  }

  /**
   * Set the PixiJS stage container that scenes are added to.
   * @param {import('pixi.js').Container} stage
   */
  setStage(stage) {
    this._stage = stage;
  }

  /**
   * Register a scene class by name.
   * @param {string} name
   * @param {Function} SceneClass - Constructor that creates a scene object
   */
  register(name, SceneClass) {
    this._scenes.set(name, SceneClass);
  }

  /**
   * Transition to a named scene with optional data.
   * Cleans up old scene, creates and initializes new one.
   * @param {string} name
   * @param {object} [data]
   */
  async transition(name, data) {
    const SceneClass = this._scenes.get(name);
    if (!SceneClass) {
      console.error(`[SceneManager] Scene "${name}" not registered.`);
      return;
    }

    // Exit and clean up old scene
    if (this._currentScene) {
      try {
        if (this._currentScene.exit) {
          this._currentScene.exit();
        }
      } catch (err) {
        console.error(`[SceneManager] Error exiting scene "${this._currentName}":`, err);
      }
      if (this._stage && this._currentScene.container) {
        this._stage.removeChild(this._currentScene.container);
      }
      // Destroy old scene container to free GPU resources
      if (this._currentScene.container) {
        this._currentScene.container.destroy({ children: true });
      }
      this._currentScene = null;
      this._currentName = null;
    }

    // Create and initialize new scene
    const scene = new SceneClass();
    this._currentScene = scene;
    this._currentName = name;

    if (scene.init) {
      await scene.init();
    }

    if (scene.enter) {
      await scene.enter(data);
    }

    if (this._stage && scene.container) {
      this._stage.addChild(scene.container);
    }

    eventBus.emit('scene:changed', { name, data });

    // Log via debug if available
    if (window.__debug && window.__debug.log) {
      window.__debug.log.event(`Scene transition: ${name}`);
    }
  }

  /**
   * Get the current active scene.
   * @returns {object|null}
   */
  getCurrentScene() {
    return this._currentScene;
  }

  /**
   * Get current scene name.
   * @returns {string|null}
   */
  getCurrentSceneName() {
    return this._currentName;
  }

  /**
   * Called every frame by GameLoop.
   * @param {number} dt - Delta time in seconds
   */
  update(dt) {
    if (this._currentScene && this._currentScene.update) {
      this._currentScene.update(dt);
    }
  }

  /**
   * Called when the viewport resizes.
   * @param {number} w
   * @param {number} h
   */
  resize(w, h) {
    if (this._currentScene && this._currentScene.resize) {
      this._currentScene.resize(w, h);
    }
  }
}

const sceneManager = new SceneManager();
export default sceneManager;
